<template>
  <slot v-if="!notice" />
  <div v-else class="flex flex-1 items-center justify-center px-4 py-12">
    <section
      role="status"
      class="flex w-full max-w-120 flex-col gap-6 rounded-3xl bg-white p-8 shadow-card"
    >
      <img
        :src="logoUrl"
        :alt="t('components.app_header.logo_alt')"
        class="h-auto w-32"
        width="107"
        height="25"
      />
      <div class="flex flex-col gap-3">
        <h1 class="font-serif text-3xl/tight font-medium text-near-black">
          {{ notice.title }}
        </h1>
        <p class="text-base text-body-text">{{ notice.message }}</p>
        <p v-if="notice.note" class="text-base text-body-text">
          {{ notice.note }}
        </p>
      </div>
      <div class="flex flex-wrap gap-3">
        <button
          v-if="actionTarget"
          type="button"
          class="rounded-pill bg-brand-green px-5 py-2.5 text-base font-medium text-white hover:opacity-90 disabled:opacity-50"
          :disabled="busy"
          @click="reactivate(notice.target)"
        >
          {{ notice.actionLabel }}
        </button>
        <button
          type="button"
          class="btn-card"
          :disabled="busy"
          @click="signOut"
        >
          {{ t("shared_ui.scheduled_deletion_gate.sign_out") }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "primevue/usetoast";
import { formatDate } from "shared-core/utils/format";
import logoUrl from "shared-ui/assets/logo.svg";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DeletionTarget } from "../../composables/use-account-deletion";
import { useDeleteRightNow } from "../../composables/use-delete-right-now";
import {
  type AuthStore,
  type AuthStoreActions,
  AuthStoreInjectKey,
} from "../../types/auth-store";
import { resolveApiErrorDetail } from "../../utils/admin-helpers";
import { useApi } from "../../utils/api";

interface DeletionNoticeInterface {
  target: DeletionTarget;
  title: string;
  message: string;
  note: string | null;
  actionLabel: string | null;
}

const i18n = useI18n();
const { t, locale } = i18n;
const te = i18n.te.bind(i18n);
const toast = useToast();
const api = useApi();
const authStore = inject<AuthStore & AuthStoreActions>(AuthStoreInjectKey)!;

const busy = ref<boolean>(false);

// Not tied to the clock: a tab left open past the deadline is signed out by the server on its next request.
function isDue(deleteAt: string): boolean {
  return new Date(deleteAt).getTime() <= Date.now();
}

function formatDeleteAt(deleteAt: string): string {
  return formatDate(deleteAt, locale.value, "long");
}

// The user's own deletion would run out unseen behind the organization's notice, so it is offered for reactivation before the organization is.
function organizationNotice(
  deleteAt: string,
  ownDeleteAt: string | null,
): DeletionNoticeInterface {
  const due = isDue(deleteAt);
  const ownPending = !due && ownDeleteAt !== null && !isDue(ownDeleteAt);
  const canReactivate =
    !due && authStore.isAdministrator && ownDeleteAt === null;
  const message = due
    ? "due"
    : authStore.isAdministrator && (canReactivate || ownPending)
      ? "organization_admin"
      : "organization_member";
  return {
    target: ownPending ? DeletionTarget.ACCOUNT : DeletionTarget.ORGANIZATION,
    title: t("shared_ui.scheduled_deletion_gate.organization_title"),
    message: t(`shared_ui.scheduled_deletion_gate.${message}`, {
      date: formatDeleteAt(deleteAt),
    }),
    note: ownPending
      ? t("shared_ui.scheduled_deletion_gate.own_account", {
          date: formatDeleteAt(ownDeleteAt),
        })
      : null,
    actionLabel: ownPending
      ? t("shared_ui.scheduled_deletion_gate.reactivate_account")
      : canReactivate
        ? t("shared_ui.scheduled_deletion_gate.reactivate_organization")
        : null,
  };
}

function accountNotice(deleteAt: string): DeletionNoticeInterface {
  const due = isDue(deleteAt);
  return {
    target: DeletionTarget.ACCOUNT,
    title: t("shared_ui.scheduled_deletion_gate.account_title"),
    message: due
      ? t("shared_ui.scheduled_deletion_gate.due")
      : t("shared_ui.scheduled_deletion_gate.account", {
          date: formatDeleteAt(deleteAt),
        }),
    note: null,
    actionLabel: due
      ? null
      : t("shared_ui.scheduled_deletion_gate.reactivate_account"),
  };
}

// The organization comes first: while it is being deleted, reactivating an account would not let anyone back in.
const notice = computed<DeletionNoticeInterface | null>(() => {
  const user = authStore.user;
  if (user?.organizationDeleteAt) {
    return organizationNotice(user.organizationDeleteAt, user.deleteAt);
  }
  return user?.deleteAt ? accountNotice(user.deleteAt) : null;
});

const actionTarget = computed<DeletionTarget | null>(() =>
  notice.value?.actionLabel && !authStore.isImpersonating
    ? notice.value.target
    : null,
);

useDeleteRightNow(actionTarget, busy);

async function reactivate(target: DeletionTarget): Promise<void> {
  busy.value = true;
  try {
    await api.delete(`${target}/deletion`);
    // The web store swallows a failed refresh, which would leave the notice up under a success message, while the Controla App throws or ends the session itself.
    await authStore.checkAuth(true, { propagateError: true });
  } catch (error) {
    console.error("Failed to reactivate after a scheduled deletion", error);
    toast.add({
      severity: "error",
      summary: t("common.error"),
      detail: resolveApiErrorDetail(
        error,
        t,
        te,
        "shared_ui.scheduled_deletion_gate.reactivate_failed",
      ),
      life: 5000,
    });
    return;
  } finally {
    busy.value = false;
  }

  toast.add({
    severity: "success",
    summary: t(`shared_ui.scheduled_deletion_gate.${target}_reactivated`),
    life: 5000,
  });
}

async function signOut(): Promise<void> {
  busy.value = true;
  try {
    await authStore.logout();
  } finally {
    busy.value = false;
  }
}
</script>
